import { Breadcrumb, Card } from "react-bootstrap";
import { useSelector } from "react-redux";
import { NavLink, useHistory, useParams } from "react-router-dom";
import styled from "styled-components";

import { Loader } from "../../common/components/core";
import { currentRolesSelector, currentUserSelector } from "../../redux/slices/auth/selectors";
import { SuperAdminRole, superAdminAccountType } from "../../types/superadmin";
import { PageTitle } from "../../types/pageTitle";
import { UserRole, UserCurrent } from "../../types/userCurrent";
import { BankAccount, BankReviewStatus } from "../../types/common";

import useAccount from "./hooks/useAccount";
import BankStatusBox, { BankStatusText } from "./components/BankStatusBox";
import AllowedFunction from "./components/AllowedFunction";
import { AccountDetailTooltip } from "./components/AccountDetailTooltip";

import { addr2Sub } from "./components/AccountForm";

const AccountDetailView = (): JSX.Element => {
    document.title = PageTitle.AccountDetailView;
    const history = useHistory();
    const currentRoles = useSelector(currentRolesSelector);
    const currentUser = useSelector(currentUserSelector);
    const accountType = superAdminAccountType(currentRoles);

    const { accountId } = useParams<{ accountId: string }>();
    const { bankAccount, isValidating } = useAccount(accountId);

    return (
        <Loader isLoading={isValidating} bgClassName="bg-body">
            {accountType && bankAccount && (
                <PureEditAccountView
                    account={bankAccount}
                    accountType={accountType}
                    currentRoles={currentRoles}
                    currentUser={currentUser}
                    onEditAccount={() =>
                        history.push(
                            `/account-setup/bank-account-details/${accountId}/edit`,
                        )
                    }
                />
            )}
        </Loader>
    );
};

type PureEditAccountViewProps = {
    accountType: SuperAdminRole;
    account: BankAccount;
    currentRoles: UserRole[];
    currentUser: UserCurrent | null;
    onEditAccount: () => void;
};

export const PureEditAccountView = ({
    accountType,
    account,
    currentRoles,
    currentUser,
    onEditAccount,
}: PureEditAccountViewProps): JSX.Element => {
    return (
        <>
            <div className="d-flex align-items-start justify-content-between">
                <Breadcrumb className="mb-2">
                    <Breadcrumb.Item linkAs="span">
                        <NavLink to="/account-setup">{`${accountType} Account Setup`}</NavLink>
                    </Breadcrumb.Item>
                    <Breadcrumb.Item active className="fw-bold">
                        {`${accountType} Bank Account Details`}
                    </Breadcrumb.Item>
                </Breadcrumb>
                <div>
                    <AllowedFunction
                        currentRoles={currentRoles}
                        currentUser={currentUser}
                        bankData={account}
                    ></AllowedFunction>
                </div>
            </div>

            <Card className="mt-5 border-0">
                <CardHeaderDivStyled>
                    <h2></h2>
                    <div>
                        <BankStatusBox status={account.status} />
                    </div>
                </CardHeaderDivStyled>
                <dl>
                    <StyledCardHeader>
                        <h1 className="mb-0 rem-1-5">{`${accountType} Bank Account Details`}</h1>
                    </StyledCardHeader>

                    <StyledCardBody>
                        <div className="mb-2">
                            <dt className="rem-1">{`${accountType} Bank Account Holder Name`}</dt>
                            <AccountDetailTooltip
                                className="fw-bold"
                                oldData={account.bankAccountHolderName}
                                newData={account.tmpBankAccount?.bankAccountHolderName}
                            ></AccountDetailTooltip>
                        </div>
                        <div className="mb-2">
                            <dt className="rem-1">{`${accountType} Bank Account Number`}</dt>
                            <AccountDetailTooltip
                                className="fw-bold"
                                oldData={account.bankAccountNumber}
                                newData={account.tmpBankAccount?.bankAccountNumber}
                            ></AccountDetailTooltip>
                        </div>
                        <div className="mb-2">
                            <dt className="rem-1">SWIFT Address</dt>
                            <AccountDetailTooltip
                                className="fw-bold"
                                oldData={account.swiftAddress}
                                newData={account.tmpBankAccount?.swiftAddress}
                            ></AccountDetailTooltip>
                        </div>
                        <div className="mb-2">
                            <dt className="rem-1">Bank Name</dt>
                            <AccountDetailTooltip
                                className="fw-bold"
                                oldData={account.bankName}
                                newData={account.tmpBankAccount?.bankName}
                            ></AccountDetailTooltip>
                        </div>

                        <div className="mb-2">
                            <dt className="rem-1">{`${accountType}'s Registered Address`}</dt>
                            <AccountDetailTooltip
                                className="fw-bold"
                                oldData={addr2Sub(account.registeredAddress, "addr")}
                                newData={account.tmpBankAccount?.registeredAddress ? addr2Sub(account.tmpBankAccount.registeredAddress, "addr") : undefined}
                            ></AccountDetailTooltip>
                        </div>
                        {(addr2Sub(account.registeredAddress, "city") || (account.tmpBankAccount && addr2Sub(account.tmpBankAccount.registeredAddress, "city"))) &&
                            <div className="mb-2">
                                <dt className="rem-1">City</dt>
                                <AccountDetailTooltip
                                    className="fw-bold"
                                    oldData={addr2Sub(account.registeredAddress, "city")}
                                    newData={account.tmpBankAccount?.registeredAddress ? addr2Sub(account.tmpBankAccount.registeredAddress, "city") : undefined}
                                ></AccountDetailTooltip>
                            </div>
                        }
                        {(addr2Sub(account.registeredAddress, "code") || (account.tmpBankAccount && addr2Sub(account.tmpBankAccount.registeredAddress, "code"))) &&
                            <div className="mb-2">
                                <dt className="rem-1">Country Code</dt>
                                <AccountDetailTooltip
                                    className="fw-bold"
                                    oldData={addr2Sub(account.registeredAddress, "code")}
                                    newData={account.tmpBankAccount?.registeredAddress ? addr2Sub(account.tmpBankAccount.registeredAddress, "code") : undefined}
                                ></AccountDetailTooltip>
                            </div>
                        }
                        <div className="mb-2">
                            <dt className="rem-1">Payment Currency</dt>
                            <AccountDetailTooltip
                                className="fw-bold"
                                oldData={account.currency}
                                newData={account.tmpBankAccount?.currency}
                            ></AccountDetailTooltip>
                        </div>
                        <div className="mb-2">
                            <dt className="rem-1">Profile Name</dt>
                            <dd className="fw-bold mb-0">{account.userProfile.name}</dd>
                        </div>
                        {account.status &&
                            <div className="mb-2">
                                <dt className="rem-1">Status</dt>
                                <dd className="fw-bold mb-0">{BankStatusText(account.status)}</dd>
                            </div>
                        }
                        {account.submitBy &&
                            <div className="mb-2">
                                <dt className="rem-1">Submitted By</dt>
                                <dd className="fw-bold mb-0">{account.submitBy}</dd>
                            </div>
                        }
                        {account.reviewBy &&
                            <div className="mb-2">
                                <dt className="rem-1">
                                    {account.reviewStatus === BankReviewStatus.APPROVED ?
                                    "Approved By" :
                                    "Rejected By"}
                                </dt>
                                <dd className="fw-bold mb-0">{account.reviewBy}</dd>
                            </div>
                        }
                        {account.rejectReason &&
                            <div className="mb-2">
                                <dt className="rem-1">Reject Reason</dt>
                                <dd className="fw-bold mb-0">{account.rejectReason}</dd>
                            </div>
                        }
                    </StyledCardBody>
                </dl>
            </Card>
        </>
    );
};

const CardHeaderDivStyled = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const StyledCardHeader = styled(Card.Header)`
    padding: 0px;
    padding-bottom: 32px;
`;

const StyledCardBody = styled(Card.Body)`
    padding: 0px;
    padding-top: 32px;
`;

export default AccountDetailView;
